import React from 'react';

import { DefaultOptionType } from 'antd/lib/select';
import classNames from 'classnames';

import { useGetCustomerOrderProductsMutation } from '@apis/customerDeliveryOrderByProduct/CustomerDeliveryOrderByProductApi';
import { GetMirAccountWarehousesResponse } from '@apis/materialsInReview/MaterialsInReview.types';
import { Button } from '@atoms/button/Button';
import { SelectAutocomplete } from '@atoms/selectAutocomplete/SelectAutocomplete';
import { initialPaginationState } from '@constants/pagination';
import filterStyles from '@organisms/locateProducts/LocateProductFilters.module.less';
import { WarehouseFilter } from '@organisms/manageInventory/MIR/shared/warehouseFilter/WarehouseFilter';
import { SelectedFilterValues } from '@organisms/trackOrders/orderList/shared/selectedFilterValues/SelectedFilterValues';
import { useUser } from 'utils/hooks/useUser';
import { GetBasicDtoPagination } from 'utils/types/api/api.types';
import {
    Pagination,
    Sorting,
    SORTING_DIRECTION
} from 'utils/types/general/general.types';

export interface LocateProductFiltersProps {
    warehousesData?: GetMirAccountWarehousesResponse;
    styles: {
        [key: string]: string;
    };
    isWarehousesLoading: boolean;
    selectedWarehouses: string[];
    onRemoveWarehouse: (itemToRemove: string) => void;
    selectedProducts: string[];
    onSelectProduct: (newSelectedProduct: DefaultOptionType) => void;
    onRemoveProduct: (itemToRemove: string) => void;
    onApplyWarehouses: (newSelectedWarehouses: string[]) => void;
    onClearFilters: () => void;
    onClickSearch: () => void;
    isSearchLoading: boolean;
    children?: React.ReactNode;
}

export const LocateProductFilters = ({
    warehousesData,
    styles,
    isWarehousesLoading,
    selectedWarehouses,
    onRemoveWarehouse,
    selectedProducts,
    onSelectProduct,
    onRemoveProduct,
    onApplyWarehouses,
    onClearFilters,
    onClickSearch,
    isSearchLoading,
    children
}: LocateProductFiltersProps) => {
    const hasSelectedWarehouses = !!selectedWarehouses?.length;
    const hasSelectedProducts = !!selectedProducts?.length;
    const hasSelectedFilters = hasSelectedWarehouses || hasSelectedProducts;

    const [getAllParts, options] = useGetCustomerOrderProductsMutation();

    const selectedFiltersData = [
        {
            onRemove: onRemoveWarehouse,
            label: 'Warehouse',
            values: selectedWarehouses,
            isSelected: hasSelectedWarehouses
        },
        {
            onRemove: onRemoveProduct,
            label: 'Product',
            values: selectedProducts,
            isSelected: hasSelectedProducts
        }
    ];

    const { accountNumber, subAccountNumber } = useUser();

    const sorting: Sorting = {
        field: 'partNumber',
        direction: SORTING_DIRECTION.ASC
    };

    const pagination: Pagination = {
        ...initialPaginationState,
        pageSize: 20
    };

    const baseApiParams: Omit<GetBasicDtoPagination, 'filter'> = {
        accountNumber,
        subAccountNumber,
        pagination,
        sorts: sorting
    };

    const searchProduct = function (searchTerm: string) {
        if (searchTerm) {
            getAllParts({
                ...baseApiParams,
                filter: searchTerm
            });
        }
    };

    const selectOptions =
        options.isLoading ||
        options.isError ||
        options.data?.data.items === undefined
            ? []
            : options.data?.data.items.map((product) => ({
                  value: product.partNumber,
                  label: (
                      <div className={filterStyles.OptionContainer}>
                          <span className={filterStyles.BoldLabel}>
                              {product.partNumber}: {product.description}
                          </span>
                          <div>
                              <span className={filterStyles.BoldLabel}>
                                  Weight (lb):&nbsp;
                              </span>
                              <span className={filterStyles.LightLabel}>
                                  {product.weight}&nbsp;&nbsp;&nbsp;&nbsp;
                              </span>
                              <span className={filterStyles.BoldLabel}>
                                  Consumable:&nbsp;
                              </span>
                              <span className={filterStyles.LightLabel}>
                                  {product.isConsumable ? 'Y' : 'N'}
                                  &nbsp;&nbsp;&nbsp;&nbsp;
                              </span>
                              <span className={filterStyles.BoldLabel}>
                                  Serialized:&nbsp;
                              </span>
                              <span className={filterStyles.LightLabel}>
                                  {product.serialized ? 'Y' : 'N'}
                              </span>
                          </div>
                      </div>
                  )
              }));

    return (
        <>
            <div
                className={classNames(
                    styles.FiltersBar,
                    styles.FiltersBar__MaxWidth
                )}
            >
                <div
                    className={classNames(
                        styles.Search,
                        styles.Search__MaxWidth
                    )}
                    data-testid="SearchInputBlock"
                >
                    <SelectAutocomplete
                        data-testid="SearchInput"
                        options={selectOptions}
                        value={null}
                        fetchOptions={searchProduct}
                        placeholder="Search by Product # or Product Description"
                        isLoading={options.isLoading}
                        onSelect={onSelectProduct}
                    />
                </div>
                <div className={styles.Filters} data-testid="FiltersBlock">
                    {
                        <WarehouseFilter
                            selectedItems={selectedWarehouses || []}
                            data={warehousesData?.data || []}
                            isDataLoading={isWarehousesLoading}
                            onApplyWarehouses={onApplyWarehouses}
                        />
                    }
                </div>
                <div>
                    <Button
                        text="Search"
                        id="SearchButton"
                        className={styles.Content__ActionBtn}
                        isLoading={isSearchLoading}
                        onClick={onClickSearch}
                        disabled={!hasSelectedProducts}
                    />
                </div>
            </div>
            <div
                className={styles.Filters__Selected}
                data-testid="SelectedFilters"
            >
                {hasSelectedFilters && (
                    <SelectedFilterValues
                        selectedFiltersData={selectedFiltersData}
                        clearAllSelectedFilters={onClearFilters}
                        clearAllDesktop={true}
                    />
                )}
            </div>
            {children}
        </>
    );
};
